// components/Dashboard/tabs/platforms/InstagramChannels.js
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import './InstagramChannels.css';

const InstagramChannels = forwardRef(({ 
  showMessage, 
  API_URL,
  onNavigateToLibrary,
  onNavigateToAutomation
}, ref) => {
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [automationRules, setAutomationRules] = useState([]);
  const [tags, setTags] = useState([]);

  useImperativeHandle(ref, () => ({
    handleRefresh: refreshAccounts,
    handleAddChannel: handleConnectInstagram
  }));

  const fetchAutomationRules = async () => {
    try {
      const response = await fetch(`${API_URL}/automation`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setAutomationRules(data.rules || []);
    } catch (error) {
      console.error('Otomasyon kuralları yüklenemedi:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(`${API_URL}/tags`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setTags(data.tags || []);
    } catch (error) {
      console.error('Etiketler yüklenemedi:', error);
    }
  };

  const findMatchingAutomationRules = (accountId) => {
    return automationRules.filter(rule => {
      if (!rule.enable_instagram_upload || rule.is_active !== 1) return false;
      if (!rule.instagram_account_ids) return false;
      try {
        const ids = typeof rule.instagram_account_ids === 'string'
          ? JSON.parse(rule.instagram_account_ids)
          : rule.instagram_account_ids;
        if (Array.isArray(ids)) return ids.includes(accountId);
      } catch (e) { }
      return false;
    });
  };

  const getAccountAutomationSchedule = (accountId) => {
    const accountRules = findMatchingAutomationRules(accountId);
    if (accountRules.length === 0) return null;

    let allTimes = [];
    accountRules.forEach(rule => {
      if (rule.daily_times) {
        try {
          const times = JSON.parse(rule.daily_times);
          if (Array.isArray(times) && times.length > 0) allTimes = [...allTimes, ...times];
        } catch (e) { }
      }
    });

    allTimes.sort((a, b) => a.hour !== b.hour ? a.hour - b.hour : a.minute - b.minute);
    const uniqueTimes = allTimes.filter((time, index, self) =>
      index === self.findIndex(t => t.hour === time.hour && t.minute === time.minute)
    );

    const now = new Date();
    let nextTimeIndex = uniqueTimes.findIndex(t =>
      t.hour > now.getHours() || (t.hour === now.getHours() && t.minute > now.getMinutes())
    );
    if (nextTimeIndex === -1 && uniqueTimes.length > 0) nextTimeIndex = 0;

    return { allTimes: uniqueTimes, nextTimeIndex, rulesCount: accountRules.length };
  };

  const getAccountActiveAutomationTags = (accountId) => {
    const accountRules = findMatchingAutomationRules(accountId);
    let allTagNames = [];
    accountRules.forEach(rule => {
      if (rule.tags) allTagNames = [...allTagNames, ...rule.tags.split(',').map(t => t.trim())];
    });
    return [...new Set(allTagNames)]
      .map(name => tags.find(tag => tag.name.toLowerCase() === name.toLowerCase()))
      .filter(Boolean);
  };

  const handleAutomationClick = (accountId, e) => {
    e.stopPropagation();
    const accountRules = findMatchingAutomationRules(accountId);
    if (accountRules.length > 0 && onNavigateToAutomation) onNavigateToAutomation(accountRules[0].id);
  };

  const handleTagClick = (tagId, e) => {
    e.stopPropagation();
    if (onNavigateToLibrary) onNavigateToLibrary(tagId);
  };

  const fetchInstagramAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/platforms/instagram/accounts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) { setInstagramAccounts([]); return; }
      const data = await response.json();
      setInstagramAccounts(data.accounts || []);
    } catch (error) {
      console.error('Instagram hesapları yüklenirken hata:', error);
      showMessage('Instagram hesapları yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectInstagram = async () => {
    try {
      setConnecting(true);
      const response = await fetch(`${API_URL}/platforms/instagram/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Bağlantı başlatılamadı');
      if (data.authUrl) {
        showMessage('Instagram\'a yönlendiriliyorsunuz...', 'info');
        window.location.href = data.authUrl;
      }
    } catch (error) {
      showMessage(error.message, 'error');
    } finally {
      setConnecting(false);
    }
  };

  const disconnectAccount = async (accountId, username) => {
    if (!window.confirm(`@${username} hesabının bağlantısını kaldırmak istediğinizden emin misiniz?`)) return;
    try {
      const response = await fetch(`${API_URL}/platforms/accounts/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Bağlantı kaldırılamadı');
      const data = await response.json();
      showMessage(data.message || `@${username} hesap bağlantısı kaldırıldı`, 'success');
      fetchInstagramAccounts();
    } catch (error) {
      showMessage('Bağlantı kaldırılamadı', 'error');
    }
  };

  const refreshAccounts = async () => {
    await fetchInstagramAccounts();
    await fetchAutomationRules();
    await fetchTags();
    showMessage('Hesaplar yenilendi', 'success');
  };

  const formatCount = (count) => {
    if (!count) return '0';
    if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
    return count.toString();
  };

  const formatLastUpdate = (dateString) => {
    if (!dateString) return 'Güncellenmemiş';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      if (diffMins < 1) return 'Şimdi';
      if (diffMins < 60) return `${diffMins} dk önce`;
      return `${diffHours} saat önce`;
    }
    if (diffDays === 1) return 'Dün';
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get('connected');
    const account = urlParams.get('account');
    const error = urlParams.get('error');
    const platform = urlParams.get('platform');
    const cancelled = urlParams.get('cancelled');

    if (platform === 'instagram') {
      if (connected === 'true' && account) {
        showMessage(`✅ @${account} başarıyla bağlandı!`, 'success');
        window.history.replaceState({}, '', window.location.pathname);
        fetchInstagramAccounts();
      } else if (cancelled === 'true') {
        showMessage('Bağlantı iptal edildi', 'info');
        window.history.replaceState({}, '', window.location.pathname);
      } else if (error) {
        const errorMessage = decodeURIComponent(error);
        showMessage(errorMessage.includes('profil limiti') ? '❌ Profil limiti aşıldı.' : errorMessage, 'error');
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);

  useEffect(() => {
    fetchInstagramAccounts();
    fetchAutomationRules();
    fetchTags();
  }, []);

  if (loading && instagramAccounts.length === 0) {
    return (
      <div className="ig-loading-state">
        <div className="ig-loading-spinner"></div>
        <p>Hesaplar yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="ig-instagram-channels-container">
      {instagramAccounts.length === 0 ? (
        <div className="ig-empty-state">
          <div className="ig-empty-icon">
            <svg viewBox="0 0 24 24" width="64" height="64" fill="currentColor">
              <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z" />
            </svg>
          </div>
          <h3>Henüz Instagram hesabı bağlanmadı</h3>
          <p>İçeriklerinizi Instagram'da paylaşmak için Business hesabınızı bağlayın</p>
          <div className="ig-info-note">
            ⚠️ Sadece Business hesapları desteklenmektedir
          </div>
          <button
            className="ig-empty-add-btn"
            onClick={handleConnectInstagram}
            disabled={connecting}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z" />
            </svg>
            {connecting ? 'Bağlanıyor...' : 'Instagram Hesabı Bağla'}
          </button>
        </div>
      ) : (
        <div className="ig-channels-grid">
          {instagramAccounts.map(account => {
            const automationData = getAccountAutomationSchedule(account._id);
            const activeTags = getAccountActiveAutomationTags(account._id);

            return (
              <div key={account._id} className="ig-channel-card">
                {/* HEADER */}
                <div className="ig-card-header">
                  <div className="ig-channel-avatar">
                    {account.profilePicture ? (
                      <img src={account.profilePicture} alt={account.username} />
                    ) : (
                      <span>{(account.username || 'I')[0].toUpperCase()}</span>
                    )}
                  </div>
                  <div className="ig-channel-info">
                    <div className="ig-channel-name-row">
                      <span className="ig-channel-name" title={account.displayName || account.username}>
                        {account.displayName || account.username}
                      </span>
                      <a
                        href={`https://instagram.com/${account.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ig-external-link"
                        onClick={(e) => e.stopPropagation()}
                        title="Instagram'da aç"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                        </svg>
                      </a>
                      <span className={`ig-status-dot ${account.isActive ? 'ig-active' : 'ig-inactive'}`}
                        title={account.isActive ? 'Aktif' : 'Pasif'} />
                    </div>
                    <a
                      href={`https://instagram.com/${account.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ig-channel-handle"
                    >
                      @{account.username}
                    </a>
                  </div>
                </div>

                {/* STATS */}
                <div className="ig-stats-row">
                  <div className="ig-stat-box">
                    <span className="ig-stat-icon">👥</span>
                    <div className="ig-stat-content">
                      <span className="ig-stat-value">{formatCount(account.followerCount)}</span>
                      <span className="ig-stat-label">Takipçi</span>
                    </div>
                  </div>
                  <div className="ig-stat-box">
                    <span className="ig-stat-icon">📷</span>
                    <div className="ig-stat-content">
                      <span className="ig-stat-value">{formatCount(account.mediaCount)}</span>
                      <span className="ig-stat-label">Gönderi</span>
                    </div>
                  </div>
                </div>

                {/* OTOMASYON */}
                {automationData && automationData.allTimes.length > 0 ? (
                  <div
                    className="ig-automation-section"
                    onClick={(e) => handleAutomationClick(account._id, e)}
                    title="Otomasyonu düzenlemek için tıklayın"
                  >
                    <div className="ig-automation-header-row">
                      <span className="ig-automation-icon">🤖</span>
                      <span className="ig-automation-label">
                        Her gün {automationData.allTimes.length} paylaşım
                        {automationData.rulesCount > 1 && ` · ${automationData.rulesCount} kural`}
                      </span>
                    </div>
                    <div className="ig-automation-times">
                      {automationData.allTimes.map((time, index) => (
                        <span
                          key={`${time.hour}-${time.minute}`}
                          className={`ig-time-chip ${index === automationData.nextTimeIndex ? 'ig-time-next' : ''}`}
                        >
                          {String(time.hour).padStart(2, '0')}:{String(time.minute).padStart(2, '0')}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="ig-automation-section ig-no-automation">
                    <span className="ig-automation-icon">🔕</span>
                    <span className="ig-no-automation-text">Otomasyon ayarlanmamış</span>
                  </div>
                )}

                {/* ETİKETLER */}
                {activeTags.length > 0 && (
                  <div className="ig-tags-section">
                    {activeTags.map(tag => (
                      <span
                        key={tag.id}
                        className="ig-tag-chip"
                        onClick={(e) => handleTagClick(tag.id, e)}
                        style={{ '--tag-color': tag.color }}
                        title={`${tag.name} etiketli içerikleri görüntüle`}
                      >
                        🏷️ {tag.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* FOOTER */}
                <div className="ig-card-footer">
                  <div className="ig-footer-info">
                    <span className="ig-footer-profile">{account.profileName || 'GetLate'}</span>
                    <span className="ig-footer-date">{formatLastUpdate(account.createdAt)}</span>
                  </div>
                  <div className="ig-footer-actions">
                    <button
                      onClick={(e) => { e.stopPropagation(); disconnectAccount(account._id, account.username); }}
                      className="ig-action-btn ig-delete-btn"
                      title="Bağlantıyı Kes"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

InstagramChannels.displayName = 'InstagramChannels';
export default InstagramChannels;